import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Grid, List, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { ListingCard } from "../ListingCard";
import { ListingList } from "../ListingList";
import ExchangeCard from "../ExchangeCard";
import RatingModal from "../RatingModal";
import { profileApi } from "../../api/profileApi";
import { exchangeApi } from "../../api/exchangeApi";
import { useAuth } from "../../contexts/AuthContext";
import { useRatingModal } from "../../hooks/useRatingModal";

export const ProfileTabs = ({ listings = [], isCurrentUser = false }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("listings");
  const [viewMode, setViewMode] = useState("grid");
  const [savedListings, setSavedListings] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [loadingExchanges, setLoadingExchanges] = useState(false);

  const {
    isModalOpen,
    pendingExchange,
    handleSubmitRating: originalHandleSubmitRating,
    handleRemindLater,
    handleCloseModal,
    openModalForExchange
  } = useRatingModal();

  // Enhanced handleSubmitRating that updates local state
  const handleSubmitRating = async (exchangeId, rating) => {
    try {
      await originalHandleSubmitRating(exchangeId, rating);
      
      // Update the local exchanges state to reflect the rating
      setExchanges(prevExchanges => 
        prevExchanges.map(exchange => {
          if (exchange._id === exchangeId) {
            const isInitiator = exchange.initiator._id === currentUser?._id;
            return {
              ...exchange,
              // Update the appropriate rating fields based on who the current user is
              [isInitiator ? 'initiatorRated' : 'recipientRated']: true,
              [isInitiator ? 'initiatorRating' : 'recipientRating']: rating
            };
          }
          return exchange;
        })
      );
    } catch (error) {
      throw error; // Re-throw to let the modal handle the error
    }
  };

  // Fetch saved listings
  useEffect(() => {
    if (activeTab === "saved" && isCurrentUser) {
      const fetchSaved = async () => {
        setLoadingSaved(true);
        try {
          const response = await profileApi.getSavedListings();
          if (response.success) {
            setSavedListings(response.listings || []);
          } else {
            setSavedListings([]);
          }
        } catch (error) {
          console.error("Failed to fetch saved listings:", error);
          setSavedListings([]);
        } finally {
          setLoadingSaved(false);
        }
      };

      fetchSaved();
    }
  }, [activeTab, isCurrentUser]);

  // Fetch completed exchanges
  useEffect(() => {
    if (activeTab === "exchanges" && isCurrentUser) {
      const fetchExchanges = async () => {
        setLoadingExchanges(true);
        try {
          const response = await exchangeApi.getUserExchanges(
            "completed",
            1,
            50
          );
          if (response.success) {
            setExchanges(response.data || []);
          } else {
            setExchanges([]);
          }
        } catch (error) {
          console.error("Failed to fetch exchanges:", error);
          setExchanges([]);
        } finally {
          setLoadingExchanges(false);
        }
      };

      fetchExchanges();
    }
  }, [activeTab, isCurrentUser]);

  console.log("Listings from profile tab", listings);
  console.log("Saved listing from profile tab", savedListings);
  console.log("Exchanges from profile tab", exchanges);

  const renderListings = (items) => {
    if (viewMode === "grid") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {items.map((listing) => (
            <ListingCard
              key={listing._id}
              id={listing._id}
              title={listing.title}
              description={listing.description}
              imageUrl={listing.image}
              location={listing.location}
              distanceBand={listing.distanceBand}
              date={new Date(listing.createdAt).toLocaleDateString()}
              category={listing.category}
              userName={`${listing.seller?.firstName || "Unknown"} ${
                listing.seller?.lastName || ""
              }`}
              userAvatar={listing.seller?.image}
              userRating={listing.seller?.rating}
              ownerId={listing.seller?._id}
              status={listing.status}
            />
          ))}
        </div>
      );
    } else {
      return (
        <div>
          {items.map((listing) => (
            <ListingList
              key={listing._id}
              id={listing._id}
              title={listing.title}
              description={listing.description}
              imageUrl={listing.image}
              location={listing.location}
              date={new Date(listing.createdAt).toLocaleDateString()}
              category={listing.category}
              userName={`${listing.seller?.firstName || "Unknown"} ${
                listing.seller?.lastName || ""
              }`}
              userAvatar={listing.seller?.image}
              userRating={listing.seller?.rating}
              ownerId={listing.seller?._id}
              status={listing.status}
            />
          ))}
        </div>
      );
    }
  };

  const renderExchanges = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {exchanges.map((exchange) => (
          <ExchangeCard
            key={exchange._id}
            exchange={exchange}
            currentUserId={currentUser?._id}
            onOpenModal={openModalForExchange}
          />
        ))}
      </div>
    );
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="animate-fade-in"
    >
      <div className="flex justify-between items-center mb-6">
        <TabsList>
          <TabsTrigger value="listings">
            {isCurrentUser ? "My Listings" : "Listings"}
          </TabsTrigger>
          {isCurrentUser && (
            <>
              <TabsTrigger value="exchanges">Past Exchanges</TabsTrigger>
              <TabsTrigger value="saved">Saved</TabsTrigger>
            </>
          )}
        </TabsList>

        <div className="flex items-center gap-6">
          {/* Only show view toggle for listings and saved tabs */}
          {(activeTab === "listings" || activeTab === "saved") && (
            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List />
              </Button>
            </div>
          )}

          {/* {isCurrentUser && (
            <Link to="/create-listing">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Listing
              </Button>
            </Link>
          )} */}
        </div>
      </div>

      <TabsContent value="listings">
        {listings.length > 0 ? (
          renderListings(listings)
        ) : (
          <div className="text-center py-16 bg-white rounded-xl border border-border/60">
            <div className="text-5xl mb-4">🌱</div>
            <h3 className="text-xl font-semibold mb-2">No Listing yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              You haven't created any listings yet. Start sharing your homegrown
              produce with your community!
            </p>
            {isCurrentUser && (
            <Link to="/create-listing">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Listing
              </Button>
            </Link>
          )}
          </div>
        )}
      </TabsContent>

      {isCurrentUser && (
        <>
          <TabsContent value="exchanges">
            {loadingExchanges ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading exchanges...</p>
              </div>
            ) : exchanges.length > 0 ? (
              renderExchanges()
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-border/60">
                <div className="text-5xl mb-4">🤝</div>
                <h3 className="text-xl font-semibold mb-2">No exchanges yet</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  You haven't made any exchanges yet. Browse listings and start
                  connecting with local growers
                </p>
                <Link to="/browse">
                  <Button>
                    Browse Listings
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Saved Listings Tab */}
          <TabsContent value="saved">
            {loadingSaved ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  Loading saved listings...
                </p>
              </div>
            ) : savedListings.length > 0 ? (
              renderListings(savedListings)
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-border/60">
                <div className="text-5xl mb-4">📖</div>
                <h3 className="text-xl font-semibold mb-2">
                  No saved listings
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  You haven't saved any listings yet. Save listings you're
                  interested in for later.
                </p>
                <Link to="/browse">
                  <Button>
                    Browse Listings
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>
        </>
      )}

      {/* Rating Modal */}
      <RatingModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        exchange={pendingExchange}
        onSubmitRating={handleSubmitRating}
        onRemindLater={handleRemindLater}
      />
    </Tabs>
  );
};